import * as Yup from 'yup'
import {TabOption} from '../../../shared/components/FormTabHeader'
import {BaseEntity} from '../../../shared/models/ApiModels'
import {SubTabOption} from '../../../shared/components/SubTabHeader'

export interface ICarModel extends BaseEntity {
  carDefinition: CarDefinition
  carDetail: CarDetail
  carInsurance: CarInsurance
  carWarning: CarWarning
}
export interface CarDefinition extends BaseEntity {
  plate: string
  brand: string
  modelType: string
  vehicleGroup: string
  transmission: string
  fuelType: string
  modelYear: number
  webReservationStatus: string
  branch: string
  location: string
  carStatus: string
  deactivationReason: string
  color: string
  lastKm: number
  lastFuel: string
  officeReservationStatus: string
}
export interface CarDetail extends BaseEntity {
  detail: string
  bodyType: string
  cleaningStatus: string
  licenseDate: Date | string
  licenseDocumentNo: string
  registrationDate: Date | string
  chassisNumber: string // unique
  engineNumber: string // unique
  insuranceFollow: string
  hasOgsHgs: string
  vehicleStatus: string
  ogsHgsNumber: string
  ogsHgsBalance: number
  radioCode: string
  enginePower: string
  cylinderVolume: number
  integrationCode: string
  autoEngineStop: string
  hgsCompany: string
  vehicleTrackingNumber: string
  periodicServiceTracking: string
}
export interface CarInsurance extends BaseEntity {
  trafficInsuranceStartDate: Date | string
  trafficInsuranceEndDate: Date | string
  insuranceCompany: string
  insuranceAgency: string
  trafficPolicyNumber: string // required field
  inspectionStartDate: Date | string
  inspectionEndDate: Date | string
  cascoStartDate: Date | string
  cascoEndDate: Date | string
  cascoCompany: string
  cascoAgency: string
  cascoPolicyNumber: string
  cascoCode: string
}
export interface CarWarning extends BaseEntity {
  fleetEntryDate?: string // ISO date format (yyyy-MM-dd)
  cascoValue?: number
  purchasingCompany?: string
  closeDate?: string // ISO date format
  pledgeStatus?: string
  vehicleOwner?: string
  vehicleOwnerCompany?: string
  vehicleOwnerNumber?: string
  secondaryVehicleOwner?: string
  purchaseTotalPrice: number // Alım Bedeli Toplam
  purchasePriceExclTax: number // Alım Bedeli Vergisiz
  purchaseVAT: number // Alım Bedeli KDV
  purchaseSCT: number // Alım Bedeli ÖTV
  purchaseDate: string // Alım Tarihi (ISO date string, e.g. "2025-08-09")
  purchaseInvoiceNumber: string // Alım Fatura No
  vehicleSecondHandPrice: number // Araç 2.El Fiyatı
  fleetManagementCost: number // Filo Yönetim Maliyeti
  monthlyCost: number // Aylık Maliyet
  additionalPrice: number // Ek (İlave) Fiyat
  creditCompany: string // Kredi Firması
  externalSupplierKmLimit: number // Dış Tedarik Km Limiti
  assistServiceProvider: string // Hizmet Veren Assist Firma
  specialCode1: string
  specialCode2: string
  specialCode3: string
  specialCode4: string
  specialCode5: string
  fleetExitDate?: string // ISO date string, Filo Çıkış Tarihi
  plannedExitDate?: string // ISO date string, Çıkması Planlanan Tarih
  saleWarningKm?: number // Satış Uyarı KM
  description?: string // Açıklama
  tireInfo?: string // Lastik Bilgisi
  snowTireStatus?: string // Kar Lastiği
  spareKeyStatus?: string // Yedek Anahtar
  tireWarningKm?: number // Lastik Uyarı KM
  lastMaintenanceNumber?: string // Son Bakım No
  lastMaintenanceKm?: number // Son Bakım Km
  lastMaintenanceDate?: Date // Son Bakım Tar.
  kmInspectionNumber?: string // Km Tespit No
  inspectionType?: string // Son Tespit Türü
  kmInspectionDate?: Date // Km Tespit Tar
  lastPeriodicNumber?: string // Son Per. No
  lastPeriodicKm?: number // Son Per. Km
  lastPeriodicDate?: Date // Son Per. Tar.
  lastTireNumber?: string // Son Lastik No
  lastTireKm?: number // Son Lastik Km
  lastTireDate?: Date // Son Lastik Tar
}

// Form types for create/update operations
export type CreateCarRequest = {
  carDefinition: Omit<CarDefinition, keyof BaseEntity>
  carDetail: Omit<CarDetail, keyof BaseEntity>
  carInsurance: Omit<CarInsurance, keyof BaseEntity>
  carWarning: Omit<CarWarning, keyof BaseEntity>
}
export type UpdateCarRequest = Partial<ICarModel>
export type CarFormData = Partial<ICarModel>

// Specific form types for each entity
export type CarDefinitionFormData = Partial<CarDefinition>
export type CarDetailFormData = Partial<CarDetail>
export type CarInsuranceFormData = Partial<CarInsurance>
export type CarWarningFormData = Partial<CarWarning>

export interface IKmFilterModel extends BaseEntity {
  plate: string
  operationType: string
  startDate: string | Date
  endDate: string | Date
  departureKm: number
  returnKm: number
  difference: number
  intermediateDifference: number
  departureOffice: string
  returnOffice: string
}

export interface IServiceFilterModel extends BaseEntity {
  plate: string
  expenseName: string
  serviceName: string
  entryKm: number
  exitKm: number
  serviceDescription: string
  date: string | Date
  entryDate: string | Date
  entryTime: string
  exitDate: string | Date
  exitTime: string
}

export interface IExpenseFilterModel extends BaseEntity {
  plate: string
  expenseName: string
  period: string
  date: string | Date
  amount: number
  paymentDate: string | Date
  payment: string
  processingCompany: string
}

export const initialCarDefinitionValues: Omit<CarDefinition, keyof BaseEntity> = {
  plate: '',
  brand: '',
  modelType: '',
  vehicleGroup: '',
  transmission: '',
  fuelType: '',
  modelYear: new Date().getFullYear(),
  webReservationStatus: 'available',
  branch: '',
  location: '',
  carStatus: 'active',
  deactivationReason: '',
  color: '',
  lastKm: 0,
  lastFuel: '',
  officeReservationStatus: 'available',
}

export const initialCarDetailValues: Omit<CarDetail, keyof BaseEntity> = {
  detail: '',
  bodyType: '',
  cleaningStatus: '',
  licenseDate: '',
  licenseDocumentNo: '',
  registrationDate: '',
  chassisNumber: '',
  engineNumber: '',
  insuranceFollow: '',
  hasOgsHgs: '0',
  vehicleStatus: '0',
  ogsHgsNumber: '',
  ogsHgsBalance: 0,
  radioCode: '',
  enginePower: '',
  cylinderVolume: 0,
  integrationCode: '',
  autoEngineStop: '0',
  hgsCompany: '',
  vehicleTrackingNumber: '',
  periodicServiceTracking: '0',
}

export const initialCarInsuranceValues: Omit<CarInsurance, keyof BaseEntity> = {
  trafficInsuranceStartDate: '',
  trafficInsuranceEndDate: '',
  insuranceCompany: '',
  insuranceAgency: '',
  trafficPolicyNumber: '',
  inspectionStartDate: '',
  inspectionEndDate: '',
  cascoStartDate: '',
  cascoEndDate: '',
  cascoCompany: '',
  cascoAgency: '',
  cascoPolicyNumber: '',
  cascoCode: '',
}

export const initialCarWarningValues: Omit<CarWarning, keyof BaseEntity> = {
  fleetEntryDate: '',
  cascoValue: 0,
  purchasingCompany: '',
  closeDate: '',
  pledgeStatus: '',
  vehicleOwner: '',
  vehicleOwnerCompany: '',
  vehicleOwnerNumber: '',
  secondaryVehicleOwner: '',
  purchaseTotalPrice: 0,
  purchasePriceExclTax: 0,
  purchaseVAT: 0,
  purchaseSCT: 0,
  purchaseDate: '',
  purchaseInvoiceNumber: '',
  vehicleSecondHandPrice: 0,
  fleetManagementCost: 0,
  monthlyCost: 0,
  additionalPrice: 0,
  creditCompany: '',
  externalSupplierKmLimit: 0,
  assistServiceProvider: '',
  specialCode1: '',
  specialCode2: '',
  specialCode3: '',
  specialCode4: '',
  specialCode5: '',
  fleetExitDate: '',
  plannedExitDate: '',
  saleWarningKm: 0,
  description: '',
  tireInfo: '',
  snowTireStatus: '0',
  spareKeyStatus: '0',
  tireWarningKm: 0,
  lastMaintenanceNumber: '',
  lastMaintenanceKm: 0,
  lastMaintenanceDate: undefined,
  kmInspectionNumber: '',
  inspectionType: '',
  kmInspectionDate: undefined,
  lastPeriodicNumber: '',
  lastPeriodicKm: 0,
  lastPeriodicDate: undefined,
  lastTireNumber: '',
  lastTireKm: 0,
  lastTireDate: undefined,
}

export const initialCarFormValues: CreateCarRequest = {
  carDefinition: initialCarDefinitionValues,
  carDetail: initialCarDetailValues,
  carInsurance: initialCarInsuranceValues,
  carWarning: initialCarWarningValues,
}

export const initialCreateCarRequest: Partial<CreateCarRequest> = {
  carDefinition: initialCarDefinitionValues,
  carDetail: initialCarDetailValues,
  carInsurance: initialCarInsuranceValues,
  carWarning: initialCarWarningValues,
}

export const validationCar = Yup.object().shape({
  plate: Yup.string().required('Plaka bilgisi zorunludur.'),
})

export interface ICarStatusFilterModel {
  plateList: string[]
  vehicleGroupIdList: string[]
  officeIdList: string[]
  dateFilterType: number | null
  startDate: Date | null
  endDate: Date | null
  balanceVisible: Boolean | null
  status: string | null
  brand: string | null
  modelType: string | null
  deactivationReason: string | null
  transmission: string | null
  fuelType: string | null
  snowTireStatus: string | null
  webReservationStatus: string | null
  officeReservationStatus: string | null
  hasOgsHgs: string | null
  hasVehicleTracking: string | null
}

export const initialCarStatusFilter: ICarStatusFilterModel = {
  plateList: [],
  vehicleGroupIdList: [],
  officeIdList: [],
  dateFilterType: null,
  startDate: new Date(),
  endDate: (() => {
    const d = new Date()
    d.setDate(d.getDate() + 365)
    return d
  })(),
  balanceVisible: null,
  status: null,
  brand: null,
  modelType: null,
  deactivationReason: null,
  transmission: null,
  fuelType: null,
  snowTireStatus: null,
  webReservationStatus: null,
  officeReservationStatus: null,
  hasOgsHgs: null,
  hasVehicleTracking: null,
}

export const validationCarStatusFilter = Yup.object().shape({})

export interface ICarFilterModel {
  carId: string | null
  status: string | null
  tireWarningKm: string | null
  vehicleGroupIdList: string[]
  officeIdList: string[]
  vehicleOwner: string | null
  dateFilterType: number | null
  startDate: Date | null
  endDate: Date | null
}

export const initialCarFilter: ICarFilterModel = {
  carId: null,
  status: null,
  tireWarningKm: null,
  vehicleGroupIdList: [],
  officeIdList: [],
  vehicleOwner: null,
  dateFilterType: null,
  startDate: new Date(),
  endDate: (() => {
    const d = new Date()
    d.setDate(d.getDate() + 365)
    return d
  })(),
}

export const validationCarFilter = Yup.object().shape({})

export interface CarFilteredDTO extends BaseEntity{
  plate: string;
  brand: string;
  modelType: string | null;
  detail: string;
  modelYear: string;
  fuelType: string;
  transmission: string;
  branch: string;
  vehicleGroup: string;
  chassisNumber: string;
  lastKm: string;
  color: string;
  snowTireStatus: string;
  licenseDocumentNo: string;
  vehicleOwner: string;
  status: string;
  vehicleTrackingNumber: string;
  radioCode: string;
  totalPurchasePrice: string | null;
  hgsNumber: string;
  engineNumber: string;
  description: string;
  fleetEntryDate: string | null;
  fleetExitDate: string | null;
  plannedExitDate: string | null;
  cascoValue: string;
}

export const carTabs: TabOption<'definition' | 'detail' | 'insurance' | 'warning' | 'storage'>[] = [
  {key: 'definition', title: 'Araç Tanımı', icon: 'bi bi-car-front'}, // araç simgesi
  {key: 'detail', title: 'Araç Detay', icon: 'bi bi-clipboard-check'}, // detay kontrol
  {key: 'insurance', title: 'Sigorta & Muayene', icon: 'bi bi-shield-check'}, // güvenlik/sigorta
  {key: 'warning', title: 'Uyarılar & Açıklamalar', icon: 'bi bi-exclamation-triangle'}, // uyarı
  // {key: 'storage', title: 'Dosya/Resim', icon: 'bi bi-folder-image'}, // dosya+resim
]

export const subTabOptions: SubTabOption<
  'fleet' | 'desc' | 'filo_detail' | 'km' | 'service' | 'expence'
>[] = [
  {key: 'fleet', title: 'Filo', icon: 'bi bi-truck'}, // filo/araç
  {key: 'desc', title: 'Açıklama', icon: 'bi bi-card-text'}, // metin/açıklama
  {key: 'filo_detail', title: 'Operasyon Detayı', icon: 'bi bi-clipboard-data'}, // detay/rapor
  {key: 'km', title: 'KM', icon: 'bi bi-speedometer2'}, // hız/kilometre
  {key: 'service', title: 'Servis', icon: 'bi bi-tools'}, // bakım/servis
  {key: 'expence', title: 'Harcama', icon: 'bi bi-cash-stack'}, // para/harcama
]

export const PASSIVE_REASON = [
  {value: '0', label: 'Satış'},
  {value: '1', label: 'Pert'},
  {value: '2', label: 'Çalınma'},
  {value: '3', label: 'Plaka Değişikliği'},
  {value: '4', label: 'Tedarikçi İade'},
  {value: '5', label: 'Diğer'},
] as const

export const COLORS = [
  {value: '0', label: 'Beyaz'},
  {value: '1', label: 'Siyah'},
  {value: '2', label: 'Kırmızı'},
] as const

export const WEHICLE_STATUS = [
  {value: '0', label: '0 Km Stok'},
  {value: '1', label: '2. El Satış'},
  {value: '2', label: 'Havuz'},
  {value: '3', label: 'KSK'},
  {value: '4', label: 'Sipariş'},
  {value: '5', label: 'Tahsis'},
  {value: '6', label: 'USK'},
] as const

export const HGS_FIRMS = [
  {value: '0', label: 'PTT'},
  {value: '1', label: 'Vakıfbank'},
  {value: '2', label: 'İş Bankası'},
] as const

export const TIRE_WARNING_KM = [{value: '0', label: 'Lastik Uyarı KM'}] as const

export const CAR_DATE_FILTER_TYPE = [
  {value: '0', label: 'Başlangıç Tarihi'}, 
  {value: '1', label: 'Bitiş Tarihi'}, 
] as const
